import React, { useState } from 'react';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react';
import { chevronForward, eyeOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import ShareContentSkeleton, {
  SHARE_STEP_HELP_TEXT,
} from './ShareContentSkeleton';
import GardenSharePage from './GardenSharePage';
import RecipientBox from './RecipientBox';
import ComposerFormTab from './ComposerFormTab';
import ComposerOptionsTab from './ComposerOptionsTab';
import TabbedPreviewSheet from './TabbedPreviewSheet';
import { Destinations } from './navigation_logic';
import styles from './ShareComposer.module.css';

type ComposerTab = 'form' | 'options';

/**
 * Who the share goes to, as one line that leads back to the picker. The
 * composer shows the recipients rather than edits them, so the whole row is
 * the way back to changing them.
 */
const RecipientRow: React.FC = () => {
  const { recipients, contactIntents, introBlockedKeys, nav } =
    useShareWizardContext();
  const toPicker = () => nav.goTo(Destinations.recipients, 'back');

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Change recipients"
      className={styles.recipients}
      data-testid="garden-composer-recipients"
      onClick={toPicker}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toPicker();
        }
      }}
    >
      <span className={styles.to}>To</span>
      <div className={styles.recipientChips}>
        {recipients.length === 0 ? (
          <span className={styles.noRecipients}>Choose people</span>
        ) : (
          <RecipientBox
            to={null}
            value={recipients.map(contact => contact.localId)}
            contactIntents={contactIntents}
            introBlockedKeys={introBlockedKeys}
            readOnly
            collapsedRows={1}
            expandable={false}
          />
        )}
      </div>
      <IonIcon
        icon={chevronForward}
        aria-hidden="true"
        className={styles.chevron}
      />
    </div>
  );
};

/**
 * The garden flow's second destination: what the share says (mock 9c).
 *
 * One surface stands where the wizard has a Changes step and a message step
 * per intent. The Form tab asks every question those steps asked, in one pass;
 * the Options tab holds how the share goes out. Above both, the recipients,
 * one tap from the picker.
 *
 * "Queue Msgs for Sharing" ("Msg" when the share sends just one message)
 * puts the share in the outbox once every message it sends is complete — and,
 * in an update, once the sender has said what changed — and leaves the flow
 * for the outbox, which sends from there on its own. The eye ahead of it opens the preview sheet over the composer, whether
 * or not the messages are finished: seeing what an unanswered question leaves
 * out is part of answering it.
 */
const ShareComposer: React.FC = () => {
  const {
    topic,
    isTopicLoading,
    topicError,
    isUpdateFlow,
    updateSummary,
    sharingMode,
    reshareInfoForPrevLeg,
    inProgressLegId,
    intentsWithRecipients,
    isTemplateHydrated,
    handleQueueToOutbox,
    sendPlan,
    nav,
  } = useShareWizardContext();
  const [tab, setTab] = useState<ComposerTab>('form');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isQueuing, setIsQueuing] = useState(false);

  const queue = async () => {
    setIsQueuing(true);
    try {
      await handleQueueToOutbox();
    } finally {
      setIsQueuing(false);
    }
  };

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  // A reshare reads on from the leg it passes, so its message cannot be
  // queued until that leg's details come back from the server.
  const reshareStoryReady =
    sharingMode !== 'reshare' || reshareInfoForPrevLeg !== null;
  const canQueue =
    inProgressLegId == null &&
    reshareStoryReady &&
    (!isUpdateFlow || updateSummary.trim() !== '') &&
    intentsWithRecipients.every(isTemplateHydrated);

  const ready = !isTopicLoading && !topicError;

  // Some ways into the flow (the welcome sheet's reshare prompt, a shared
  // link) skip the share dock's guard, so the composer checks share:topics
  // itself and shows what the sender still owes in place of the form.
  return (
    <GardenSharePage title="Message">
      <ActionGuardContainer>
        <ActionGuard
          disableMode="hidden"
          optimistic
          action={ACTIONS.SHARE}
          resource={RESOURCES.TOPICS}
          resourceId={topic?.id}
          helpText={SHARE_STEP_HELP_TEXT}
          fallbackElem={lock => <ShareContentSkeleton lock={lock} />}
          guardedElem={
            <>
              <IonContent
                style={{ '--background': 'var(--app-page-background)' }}
              >
                <div className={styles.body} data-testid="garden-composer">
                  {isTopicLoading ? (
                    <div className={styles.status}>
                      <IonSpinner />
                    </div>
                  ) : topicError ? (
                    <div className={styles.status}>
                      <p className={styles.error}>{topicError}</p>
                      <IonButton onClick={nav.exit} fill="clear">
                        Close
                      </IonButton>
                    </div>
                  ) : (
                    <>
                      <IonSegment
                        value={tab}
                        onIonChange={e =>
                          setTab(
                            e.detail.value === 'options' ? 'options' : 'form'
                          )
                        }
                      >
                        <IonSegmentButton
                          value="form"
                          data-testid="garden-composer-tab-form"
                        >
                          <IonLabel>Form</IonLabel>
                        </IonSegmentButton>
                        <IonSegmentButton
                          value="options"
                          data-testid="garden-composer-tab-options"
                        >
                          <IonLabel>Options</IonLabel>
                        </IonSegmentButton>
                      </IonSegment>
                      <RecipientRow />
                      {tab === 'form' ? (
                        <ComposerFormTab />
                      ) : (
                        <ComposerOptionsTab />
                      )}
                    </>
                  )}
                </div>
              </IonContent>
              {ready && (
                <FooterDock background="opaque">
                  <FooterDockRow landmark>
                    <div className={styles.actions}>
                      <IonButton
                        fill="outline"
                        shape="round"
                        className={styles.preview}
                        aria-label="Preview messages"
                        onClick={() => setIsPreviewOpen(true)}
                        data-testid="garden-composer-preview"
                      >
                        <IonIcon slot="icon-only" icon={eyeOutline} />
                      </IonButton>
                      <SmartCaseButton
                        expand="block"
                        className={styles.queue}
                        disabled={!canQueue || isQueuing}
                        onClick={queue}
                        data-testid="garden-composer-continue"
                      >
                        {sendPlan.length === 1
                          ? 'Queue msg for sharing'
                          : 'Queue msgs for sharing'}
                      </SmartCaseButton>
                    </div>
                  </FooterDockRow>
                </FooterDock>
              )}
              {ready && (
                <TabbedPreviewSheet
                  isOpen={isPreviewOpen}
                  onDismiss={() => setIsPreviewOpen(false)}
                />
              )}
            </>
          }
        />
      </ActionGuardContainer>
    </GardenSharePage>
  );
};

export default ShareComposer;
